/**
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {ElGordosRevenge} from "../ElGordosRevenge";
import {ElGordosRevengeSymbolState} from "./ElGordosRevengeSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {ShootableItem} from "../view/shooting/ShootableItem";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {ElGordosRevengeSound} from "../sound/ElGordosRevengeSound";
import {ElGordosRevengeGameAssets} from "../ElGordosRevengeGameAssets";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {CylinderFeatureName} from "../server/CylinderFeatureName";
import {ElGordosRevengeLayers, Layers} from "../view/ElGordosRevengeLayers";
import {ElGordosRevengeGameConfig} from "../ElGordosRevengeGameConfig";

export class ElGordosRevengeSymbol extends ShootableItem implements IBaseSymbol {
    public static DEFAULT_WIDTH:number = 114;
    public static DEFAULT_HEIGHT:number = 176;

    public symbolWidth:number = 114;
    public symbolHeight:number = 176;
    public bottomPadding:number = 0;
    public removed:boolean = false;
    public splitSplashDelay:number = 0.15;
    public symbolSize:number = 1;
    public symbolWays:number = 1;
    public symbolName:string;
    public stackedSymbolName:IParsedStackedSymbolName;
    public reelId:number;
    protected _xRIPAnimationName:string = "xRIP";
    protected _noWinTint:number = 0x6F6666;
    protected _xRipTint:number = 0x999999;
    protected _splitSymbolAnimationDuration:number = 0.1;
    protected _parts:MultiSprites[];
    protected _centerPosition:PIXI.Point;
    protected _deadContainer:PIXI.Container;
    protected _deadCharacter:PIXI.spine.Spine;
    protected _xRipPatch:PIXI.spine.Spine;
    protected _splitContainer:PIXI.Container;
    protected _multiplierContainer:PIXI.Container;
    protected _multiplierText:PIXI.BitmapText;
    protected _waysText:PIXI.BitmapText;
    protected _wildPatches:PIXI.Container[] = [];
    protected _partPatchContainer:PIXI.Container;
    protected _isWild:boolean;

    protected _currentMultiplier:number = 0;

    public get currentMultiplier() {
        return this._currentMultiplier;
    }

    public get symbolStep():number {
        return this.symbolHeight + this.bottomPadding;
    }

    public get actualSymbolHeight():number {
        return this.symbolHeight;
    }

    public get isWild():boolean {
        return this._isWild;
    }

    constructor(symbolName:string, reelId:number, initState:ElGordosRevengeSymbolState) {
        super({
            name : symbolName,
            target : new PIXI.Sprite(),
            holeScale : 1,
            view0 : new PIXI.Container(),
            view1 : new PIXI.Container(),
            soundPlayer : (stereoIndex:number) => this.playGunShotSound(stereoIndex),
            holeGetter : () => ShootableItem.getDefaultWoodHole(0.6),
            sparkGetter : () => ShootableItem.getDefaultWoodSpark(1),
            killabled : true
        });
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.initAnimation();
        this.getStateAnimation(initState, {duration : 0, alpha : 1}, {duration : 0});
    }

    protected playGunShotSound(stereoIndex:number):TimelineLite {
        return ElGordosRevengeSound.getDefaultGunShotWoodTimeline(stereoIndex);
    }

    protected initAnimation():void {
        this._centerPosition = this.getCenterPosition();
        this._parts = [];
        const part = new MultiSprites();
        part.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(part);
        this._parts.push(part);

        this._multiplierContainer = new PIXI.Container();
        this._multiplierContainer.scale.set(1);
        this._multiplierContainer.position.set(this._centerPosition.x, this._centerPosition.y + 22);
        this._multiplierText = this.getMultiplierText();
        this._multiplierText.x = this.symbolWidth * 0.5 - 8;
        this._waysText = this.getWaysText();
        this._waysText.x = -this.symbolWidth * 0.5 + 8;
        this._multiplierContainer.addChild(this._multiplierText, this._waysText);
    }

    protected getMultiplierText():PIXI.BitmapText {
        const text = new PIXI.BitmapText("", {
            fontName : ElGordosRevengeGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER,
            fontSize : 24,
            tint : ElGordosRevengeGameConfig.MULTIPLIER_TEXT_TINT
        });
        (<PIXI.Point>text.anchor).set(1, 1.38);
        text.letterSpacing = -8;
        return text;
    }

    protected getWaysText():PIXI.BitmapText {
        const text = new PIXI.BitmapText("", {
            fontName : ElGordosRevengeGameAssets.BITMAP_FONT_WAYS,
            fontSize : 19,
            tint : ElGordosRevengeGameConfig.WAYS_NUMBER_TINT
        });
        (<PIXI.Point>text.anchor).set(0, 1.38);
        text.letterSpacing = -8;
        return text;
    }

    protected getCenterPosition():PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public getSymbolShotRect():PIXI.Rectangle {
        return new PIXI.Rectangle(-45, -70, 90, 140);
    }

    public getStateAnimation(state:ElGordosRevengeSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):TimelineLite {
        this.createState(state);
        const tl = new TimelineLite();
        this._parts.forEach(part => {
            tl.add(part.playSprite(state, inVars, outVars, frames), 0);
        });
        if(state == ElGordosRevengeSymbolState.NO_WIN || state == ElGordosRevengeSymbolState.XRIP) {
            tl.add(() => {
                if(this._deadCharacter) {
                    const entry = this._deadCharacter.state.getCurrent(1);
                    if(entry) {
                        entry.trackTime = entry.animationEnd;
                    }
                }
                if(this._wildPatches.length > 0) {
                    const newPatches:PIXI.Container[] = [];
                    this._wildPatches.forEach(w => {
                        const newW = new PIXI.Sprite(ElGordosRevenge.assetLoader.getTextures("S2/Wild_Fire")[0]);
                        newW.anchor.set(0.5, 0.5);
                        newW.scale.set(w.scale.x);
                        newW.position.set(w.x, w.y);
                        newPatches.push(newW);
                        this._partPatchContainer.addChild(newW);
                        this._partPatchContainer.removeChild(w);
                        w.destroy();
                    });
                    this._wildPatches = [];
                    this._wildPatches = newPatches.concat();

                }

                this.getStateColorChangeAnimation(state)
            }, 0);
        }

        return tl;
    }

    protected getStateColorChangeAnimation(stateName?:string):void {
        // No need to tint wildPath since it can only be no win state in reel 5 inside the reel frame, not inside symbol.
        if(!stateName) {
            stateName = this._parts[0].currentSpriteName;
        }
        const tint = stateName === ElGordosRevengeSymbolState.NO_WIN ? this._noWinTint : (stateName === ElGordosRevengeSymbolState.XRIP ? this._xRipTint : 0xffffff);

        if(this._shootContainer) {
            this._shootContainer.children.forEach(c => {
                if(c instanceof PIXI.Sprite) {
                    c.tint = tint;
                }
            });
        }

        if(this._wildPatches) {
            this._wildPatches.forEach(p => {
                if((<any>p)["tint"]) {
                    (<any>p)["tint"] = tint
                }
            });
        }

        if(this._deadCharacter) {
            this._deadCharacter.tint = tint;
        }

        if(this._multiplierText) {
            const multiTint = tint === 0xFFFFFF ? ElGordosRevengeGameConfig.MULTIPLIER_TEXT_TINT : ElGordosRevengeGameConfig.WAYS_NUMBER_TINT + tint;
            this._multiplierText.tint = multiTint;
        }

        if(this._waysText) {
            const waysTint = tint === 0xFFFFFF ? ElGordosRevengeGameConfig.WAYS_NUMBER_TINT : ElGordosRevengeGameConfig.WAYS_NUMBER_TINT + tint;
            this._waysText.tint = waysTint;
        }

        if(this._splitContainer) {
            this._splitContainer.children.forEach(c => {
                if(c instanceof PIXI.Sprite) {
                    c.tint = tint;
                }
            });
        }
    }

    protected createState(state:ElGordosRevengeSymbolState):void {
        let updateLayers:boolean = false;
        this._parts.forEach(part => {
            if(!part.sprites[state]) {
                part.addSprites({
                    name : state,
                    textures : this.getStateTexture(state),
                    anchor : new PIXI.Point(0.5, 0.5)
                });
                if(state === ElGordosRevengeSymbolState.NO_WIN) {
                    part.sprites[state].tint = this._noWinTint;
                }
                if(state === ElGordosRevengeSymbolState.XRIP) {
                    part.sprites[state].tint = this._xRipTint;
                }
                updateLayers = true;
            }
        })
        if(updateLayers) {
            this.updateLayers();
        }
    }

    protected updateLayers():void {
        if(this._deadContainer) {
            this.addChild(this._deadContainer);
        }
        if(this._partPatchContainer) {
            this.addChild(this._partPatchContainer);
        }
        if(this._splitContainer) {
            this.addChild(this._splitContainer);
        }
        if(this._shootContainer) {
            this.addChild(this._shootContainer);
        }
        if(this._multiplierContainer) {
            this.addChild(this._multiplierContainer);
        }
        if(this._xRipPatch) {
            this.addChild(this._xRipPatch);
        }
    }

    protected getStateTexture(state?:ElGordosRevengeSymbolState):PIXI.Texture[] {
        let texturePath = `${this.symbolName}/${this.symbolName}`;
        if(this.symbolSize > 1) {
            texturePath += `_S${this.symbolSize}`;
        }
        if(state === ElGordosRevengeSymbolState.SPIN) {
            texturePath += `_${state}`;
        }
        return ElGordosRevenge.assetLoader.getTextures(texturePath);
    }

    public onRemove():void {
        if(this._deadCharacter) {
            this._deadCharacter.state.setEmptyAnimations(0);
            if(this._deadCharacter.parent) {
                this._deadCharacter.parent.removeChild(this._deadCharacter);
            }
            this._deadCharacter.destroy();
        }

        super.onRemove();
        this.destroy({children : true});
        this.removed = true;
    }

    public getGlobalCenterPosition():PIXI.Point {
        if(this.parent) {
            return new PIXI.Point(this.position.x + this._centerPosition.x + this.parent.x,
                this.position.y + this._centerPosition.y + this.parent.y);
        }
        return new PIXI.Point(this.position.x + this._centerPosition.x,
            this.position.y + this._centerPosition.y);
    }

    protected createDeadContainer():void {
        if(!this._deadContainer) {
            this._deadContainer = new PIXI.Container();
            this._deadContainer.position.set(this._centerPosition.x, this._centerPosition.y);
            this._deadCharacter = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset(this.getCharacterAssetName()));
            this._deadCharacter.autoUpdate = true;
            this._deadContainer.addChild(this._deadCharacter);
            this._deadCharacter.visible = false;

            this.updateLayers();
        }
    }

    public shotAtRandomPosition(isFeatureShoot:boolean = false, alwaysReset:boolean = false, animate:boolean = true,
                                splashGetter?:() => PIXI.Sprite, holeGetter?:() => PIXI.Sprite, sparkGetter?:() => TimelineSprite):TimelineLite {
        const rect = this.getSymbolShotRect();
        const position = new PIXI.Point(MathHelper.randomIntInRange(rect.x, rect.width + rect.x), MathHelper.randomIntInRange(rect.y, rect.height + rect.y));
        const symbolGlobalPosition = this.getGlobalCenterPosition();
        symbolGlobalPosition.x += position.x;
        symbolGlobalPosition.y += position.y;
        return this.shotAt([position, symbolGlobalPosition], false, isFeatureShoot, alwaysReset, animate, splashGetter, holeGetter, sparkGetter);
    }

    protected getCharacterAssetName():string {
        return "L";
    }

    protected getKillAnimationName():string {
        return `${this.stackedSymbolName.symName}_Dead`;
    }

    protected getDeadLoopAnimationName():string {
        return ``;
    }

    protected getIndividualAnimationName():string {
        return `${this.stackedSymbolName.symName}_Toggle`;
    }

    public playOnShot():TimelineLite {
        const tl = super.playOnShot();
        // TODO: temp
        tl.add(() => {}, 0.3);
        return tl;
        // temp end
        this.createDeadContainer();
        // const aniName = this.getOnShotAnimationName();
        tl.add(() => {
            this._deadCharacter.visible = true;
            // this._deadCharacter.state.setAnimation(0, aniName, false);
        });
        // tl.add(() => {}, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        return tl;
    }

    protected playKill():TimelineLite {
        this.createDeadContainer();
        const tl = new TimelineLite();
        const aniName = this.getKillAnimationName();
        tl.add(() => {
            this._deadCharacter.visible = true;
            this._deadCharacter.state.setAnimation(0, aniName, false);
            this.playKillSound();
        });
        tl.add(() => {
            if(this.removed) {
                return;
            }
            if(this._parts[0].currentSpriteName === ElGordosRevengeSymbolState.NORMAL) {
                const loopAniName = this.getDeadLoopAnimationName();
                if(loopAniName.length > 0) {
                    this._deadCharacter.state.setAnimation(0, loopAniName, true);
                }
            } else {
                this.getStateColorChangeAnimation();
            }
        }, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        return tl;
    }

    protected playKillSound():void {}

    public playIndividualKill():TimelineLite {
        this.createDeadContainer();
        const tl = new TimelineLite();
        const aniName = this.getIndividualAnimationName();
        tl.add(() => {
            if(!this.removed) {
                this._deadCharacter.visible = true;
                this._deadCharacter.state.setAnimation(0, aniName, false);
            }
        });
        tl.add(() => {
            if(!this.removed) {
                const loopAniName = this.getDeadLoopAnimationName();
                if(loopAniName.length > 0) {
                    this._deadCharacter.state.setAnimation(0, loopAniName, true);
                }
            }
        }, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        return tl;
    }

    public playXRip():TimelineLite {
        if(!this._xRipPatch) {
            this._xRipPatch = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset("xRIP"));
            this._xRipPatch.autoUpdate = true;
            this._xRipPatch.position.set(this._centerPosition.x, this._centerPosition.y);
            this.updateLayers();
        }

        const tl = new TimelineLite();
        // tl.add(() => {
        //     this._xRipPatch.visible = true;
        // });
        tl.add(() => {
            this._xRipPatch.state.setAnimation(0, this._xRIPAnimationName, false);
        });
        tl.add(() => {}, `+=${this._xRipPatch.spineData.findAnimation(this._xRIPAnimationName).duration}`)
        tl.add(this.getStateAnimation(ElGordosRevengeSymbolState.XRIP,
            {duration : 0.5, alpha : 1},
            {duration : 0.5, alpha : 0}), 0);
        return tl;
    }

    // type = 0 reel split, type = 1 xSplit
    public split(type:number, noWin:boolean):TimelineLite {
        const tl = new TimelineLite();
        const oldSize = this.symbolSize;
        this.symbolSize *= 2;
        this.addSplitSymbolAnimation(tl, oldSize, type, noWin);
        this.updateLayers();
        return tl;
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number, noWin:boolean):void {
        const oldParts:PIXI.Container[] = this._wildPatches.concat(this._parts);
        this._wildPatches = [];
        this._parts = [];
        if(!this._splitContainer) {
            this._splitContainer = new PIXI.Container();
        }
        for(let i = 0; i < this.symbolSize; i++) {
            const part = new MultiSprites();
            part.y = this._centerPosition.y - this.actualSymbolHeight * 0.5 + (i + 0.5) * this.actualSymbolHeight / this.symbolSize;
            part.alpha = 0;
            this.addChild(part);
            this._parts.push(part);
            tl.add(() => {
                this.addSplitPartOverlay(part);
            }, noWin ? 0.01 : this.splitSplashDelay);
            if(i > 0 && i % 2 != 0) {
                const cut = new PIXI.Sprite(ElGordosRevenge.assetLoader.getTextures("splitdivider")[0]);
                cut.anchor.set(0.5);
                cut.position.set(this._centerPosition.x, part.y - 0.5 * this.actualSymbolHeight / this.symbolSize);
                tl.add(() => {
                    this._splitContainer.addChild(cut);
                }, noWin ? 0.01 : this.splitSplashDelay);
                if(!noWin) {
                    const splitSpine = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset("split"));
                    splitSpine.autoUpdate = true;
                    const globalPosition = this.getGlobalCenterPosition();
                    splitSpine.position.set(globalPosition.x + cut.position.x, globalPosition.y + cut.position.y);
                    tl.add(() => {
                        ElGordosRevengeLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(splitSpine);
                        splitSpine.state.setAnimation(0, "animation", false);
                    }, 0);
                    tl.add(() => {
                        splitSpine.parent.removeChild(splitSpine);
                        splitSpine.destroy();
                    }, splitSpine.spineData.findAnimation("animation").duration);
                }
            }
        }
        tl.add(this.getStateAnimation(ElGordosRevengeSymbolState.NORMAL,
            {duration : 0, alpha : 1},
            {duration : 0, alpha : 0}), 0);
        oldParts.forEach(part => {
            tl.add(() => {
                part.parent.removeChild(part);
                part.destroy();
            }, noWin ? 0.01 : this.splitSplashDelay);
        });
        tl.add(() => {
            if(this._deadCharacter) {
                this._deadCharacter.visible = false;
            }
        }, noWin ? 0.01 : this.splitSplashDelay);

        this._parts.forEach((part, index) => {
            if(type === 0) { // axe
                tl.add([
                    () => {
                        part.y = index === 0 ? -30 : -15;
                        part.alpha = index === 0 ? 1 : 0;
                    },
                    TweenLite.to(
                        part,
                        noWin ? 0 : this._splitSymbolAnimationDuration,
                        {
                            alpha : 1,
                            y : part.y
                        })
                ], noWin ? 0.01 : this.splitSplashDelay);
            } else {
                const partTl = new TimelineLite();
                if(!noWin) {
                    partTl.add(TweenLite.to(part, 0.033, {x : 3}));
                    partTl.add(TweenLite.to(part, 0.433, {x : 5}));
                }
                partTl.add(TweenLite.to(part, noWin ? 0.01 : 0.1, {x : 0}));
                tl.add([
                    () => {part.alpha = 1},
                ], noWin ? 0.01 : this.splitSplashDelay);
            }
        });
        this.updateLayers();
    }

    protected addSplitPartOverlay(part:MultiSprites):TimelineLite {
        if(!this._isWild) {
            return new TimelineLite();
        }
        if(!this._partPatchContainer) {
            this._partPatchContainer = new PIXI.Container();
            this.updateLayers();
        }
        return this.addWildToPart(part, false);
    }

    public playCylinderFeature(reelId:number, symId:number, featureName:string):TimelineLite {
        const tl = new TimelineLite();
        switch(featureName) {
            case CylinderFeatureName.XSPLIT:
            case CylinderFeatureName.IN_XSPLIT:
                tl.add(this.cylinderSplit(reelId, symId, false));
                return tl;
            case CylinderFeatureName.TURN_WILD:
            case CylinderFeatureName.IN_WILD:
                tl.add(this.showWild(false));
                return tl;
            default:
                debugger;
                return tl;
        }
    }

    public restoreCylinderFeature(reelId:number, symId:number, featureName:string):void {
        switch(featureName) {
            case CylinderFeatureName.XSPLIT:
            case CylinderFeatureName.IN_XSPLIT:
                this.cylinderSplit(reelId, symId, true);
                return;
            case CylinderFeatureName.TURN_WILD:
            case CylinderFeatureName.IN_WILD:
                this.showWild(true);
                return;
            default:
                debugger;
                return;
        }
    }

    public cylinderSplit(reelId:number, symbolId:number, isRestore:boolean):TimelineLite {
        return this.showWays(reelId, symbolId, isRestore);
    }

    public showWays(reelId:number, symbolId:number, isRestore:boolean):TimelineLite {
        // Logger.logDev(`-----cylinder show ways at [${reelId}, ${symbolId}]`);
        const tl = new TimelineLite();
        tl.add(() => {
            if(!isRestore) {
                ElGordosRevenge.ways.updateSymbolWays(reelId, symbolId, ElGordosRevenge.ways.getSymbolValue(reelId, symbolId) * 2, true);
            }
            this.symbolWays *= 2;
            this._waysText.text = `x${this.symbolWays}`;
            // Logger.logDev(`-----cylinder show ways at [${reelId}, ${symbolId}, ways = ${this.symbolWays}]`);
            this.resizeWays();
            this.updateLayers();
        });
        if(!isRestore) {
            tl.add([
                gsap.to(this._multiplierContainer.scale, {x : 1.5, y : 1.3, duration : 0.1}),
                // gsap.to(this._multiplierGlow, {alpha : 1, duration : 0.1})
            ]);
            tl.add([
                gsap.to(this._multiplierContainer.scale, {x : 1, y : 1, duration : 0.5}),
                // gsap.to(this._multiplierGlow, {alpha : 0, duration : 0.5})
            ]);
        }

        return tl;
    }

    public showMultiplier(value:number, animate:boolean):TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => {
            this._multiplierText.text = `x${value}`;
            this.resizeMultiplier();
            this.updateLayers();
        });
        if(animate) {
            tl.add(() => {
                this._multiplierContainer.scale.set(0.75, 0.525);
            });
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.166, {x : 1.25, y : 1.25}));
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.133, {x : 0.95, y : 0.95}));
            tl.add(TweenLite.to(this._multiplierContainer.scale, 0.133, {x : 1, y : 1}));
            // tl.add(TweenLite.to(this._multiplierContainer.scale, 0.133, {x : 1, y : 1}));
            // tl.add([
            //     gsap.to(this._multiplierContainer.scale, {x : 1.5, y : 1.3, duration : 0.1}),
            //     // gsap.to(this._multiplierGlow, {alpha : 1, duration : 0.1})
            // ]);
            // tl.add([
            //     gsap.to(this._multiplierContainer.scale, {x : 1, y : 1, duration : 0.5}),
            //     // gsap.to(this._multiplierGlow, {alpha : 0, duration : 0.5})
            // ]);
        }
        return tl;
    }

    private resizeMultiplier():void {
        this._multiplierText.scale.set(1, 0.7);
        this._multiplierText.updateTransform();
        if(this._waysText.textWidth > 0) {
            if(this.resizeTexts()) {
                return;
            }
        }
        if(this._multiplierText.textWidth > 80) {
            const targetScale = MathHelper.roundToDecimals(80 / this._multiplierText.textWidth, 2);
            this._multiplierText.scale.set(targetScale, targetScale * 0.7);
        }
    }

    private resizeWays():void {
        this._waysText.scale.set(1);
        this._waysText.updateTransform();
        if(this._multiplierText.textWidth > 0) {
            if(this.resizeTexts()) {
                return;
            }
        }
        if(this._waysText.textWidth > 80) {
            const targetScale = MathHelper.roundToDecimals(80 / this._waysText.textWidth, 2);
            this._waysText.scale.set(targetScale);
        }
    }

    private resizeTexts():boolean {
        const totalWidth = this._waysText.textWidth + this._multiplierText.textWidth + 10;
        if(totalWidth > 100) {
            const targetScale = MathHelper.roundToDecimals(100 / totalWidth, 2);
            this._multiplierText.scale.set(targetScale, targetScale * 0.7);
            this._waysText.scale.set(targetScale);
            return true;
        }
        return false;
    }

    public showWild(isRestore:boolean):TimelineLite {
        this._isWild = true;
        if(!this._partPatchContainer) {
            this._partPatchContainer = new PIXI.Container();
            this.updateLayers();
        }
        const tl = new TimelineLite();
        this._parts.forEach(part => {
            tl.add(this.addWildToPart(part, isRestore), 0);
        });
        return tl;
    }

    protected addWildToPart(part:MultiSprites, isRestore:boolean):TimelineLite {
        const w = new PIXI.spine.Spine(ElGordosRevenge.assetLoader.getSpineAsset("Wild"));
        w.autoUpdate = true;
        w.position.set(part.x, part.y + this.getWildOffsetY());
        w.scale.set(this.getWildScale());
        this._wildPatches.push(w);
        const tl = new TimelineLite();
        if(isRestore) {
            tl.add(() => {
                this._partPatchContainer.addChild(w);
                w.state.setAnimation(0, "loop", true);
            });
            return tl;
        }
        tl.add(() => {
            this._partPatchContainer.addChild(w);
            w.state.setAnimation(0, "intro", false);
        });
        tl.add(() => {
            if(w && w.state) {
                w.state.setAnimation(0, "loop", true);
            }
        }, `+=${w.spineData.findAnimation("intro").duration}`)
        return tl;
    }

    protected getWildOffsetY():number {
        return -4;
    }

    protected getWildScale():number {
        return this.symbolSize == 2 ? 0.7 : 0.8;
    }
}